<template>
  <div class="animated fadeIn page-shell">
    <div class="page-main">
      <header-padrao class="mb-2" icon="fa fa-check-square-o" titulo="CONFERÊNCIA FINANCEIRA">
        <template #subtitulo>
          <div class="d-flex align-items-center flex-wrap">
            <span class="mr-2">
              <i class="fa fa-key mr-1 text-info"></i>
              ID: <strong class="text-white">{{ registro.ID || 'Novo' }}</strong>
            </span>
            <span class="mr-2" v-if="registro.STATUS">
              <b-badge :variant="getVariantStatus(registro.STATUS)" pill>
                {{ getLabelStatus(registro.STATUS) }}
              </b-badge>
            </span>
            <span v-if="registro.CONTA_ID" class="mr-2">
              <i class="fa fa-university mr-1 text-info"></i>
              <strong class="text-white">{{ registro.CONTA_CODIGONOME || registro.CONTA_NOME || registro.CONTA_ID }}</strong>
            </span>
          </div>
        </template>

        <template #acoes>
          <b-button v-if="registro.CONTA_ID" variant="link" class="btn-glass btn-glass-info px-4 font-weight-bold" :disabled="!podeCarregarResumo || carregandoResumo" @click.stop.prevent="carregarResumo">
            <i class="fa fa-refresh mr-1"></i> CARREGAR RESUMO
          </b-button>
          <b-button v-if="itens.length > 0" variant="link" class="btn-glass btn-glass-secondary px-4 font-weight-bold ml-2" @click.stop.prevent="imprimirEspelhoConferencia">
            <i class="fa fa-file-text-o mr-1"></i> ESPELHO
          </b-button>
          <b-button variant="link" class="btn-glass btn-glass-success px-4 font-weight-bold ml-2" :disabled="salvando || !podeSalvar" @click.stop.prevent="salvar()">
            <i class="fa fa-save mr-1"></i> {{ registro.ID ? 'SALVAR' : 'GRAVAR' }}
          </b-button>
          <b-button variant="link" class="btn-glass btn-glass-primary px-4 font-weight-bold ml-2" :disabled="confirmando || !podeConfirmar" @click.stop.prevent="abrirModalConfirmar">
            <i class="fa fa-lock mr-1"></i> CONFIRMAR
          </b-button>
          <b-button v-if="podeReabrir" variant="link" class="btn-glass btn-glass-warning px-4 font-weight-bold ml-2" :disabled="reabrindo" @click.stop.prevent="abrirModalReabrir">
            <i class="fa fa-unlock-alt mr-1"></i> REABRIR
          </b-button>
          <b-button v-if="registro.ID && registro.STATUS === statusConferencia.ABERTA" variant="link" class="btn-glass btn-glass-danger px-4 font-weight-bold ml-2" @click.stop.prevent="abrirModalCancelar">
            <i class="fa fa-ban mr-1"></i> CANCELAR
          </b-button>
          <b-button variant="light" pill class="ml-2 font-weight-bold shadow-sm px-4" @click.stop.prevent="voltar">
            <i class="fa fa-arrow-left mr-1"></i> Voltar
          </b-button>
        </template>
      </header-padrao>

      <b-row class="px-2">
        <b-col sm="12">
          <b-card class="shadow-sm mb-3" :class="{'bg-dark border-secondary': isDark}">
            <h5 class="mb-3">
              <i class="fa fa-info-circle mr-2 text-primary"></i>
              Dados da Conferência
            </h5>
            <b-row>
              <b-col md="4">
                <label class="font-weight-bold">Conta *</label>
                <ContaDropDown v-model="registro.CONTA_ID" :disabled="!podeEditarCabecalho || !!registro.ID" />
              </b-col>
              <b-col v-if="registro.CONTA_ID" md="3">
                <label class="font-weight-bold">Data Inicial *</label>
                <CecInputData v-model="registro.DATA_INICIO" :disabled="!podeEditarPeriodo" />
              </b-col>
              <b-col v-if="registro.CONTA_ID" md="3">
                <label class="font-weight-bold">Data Final *</label>
                <CecInputData v-model="registro.DATA_FIM" :disabled="!podeEditarDataFinal" />
              </b-col>
              <b-col md="2">
                <label class="font-weight-bold">Status</label>
                <b-form-input :value="getLabelStatus(registro.STATUS)" disabled class="shadow-xs rounded-pill"></b-form-input>
              </b-col>
            </b-row>
            <b-row class="mt-2">
              <b-col md="12">
                <small class="text-muted d-block">{{ mensagemPeriodoSugerido }}</small>
              </b-col>
            </b-row>
            <b-row class="mt-2">
              <b-col md="12">
                <label class="font-weight-bold">Observação</label>
                <b-form-textarea v-model="registro.OBS" rows="2" class="shadow-xs rounded" :disabled="!podeEditarCabecalho"></b-form-textarea>
              </b-col>
            </b-row>
          </b-card>

          <b-row>
            <b-col md="3">
              <div class="summary-card summary-card-success shadow-sm mb-3">
                <small class="summary-label">Crédito Sistema</small>
                <h5 class="mb-0 font-weight-bold text-success">{{ formatarMoeda(registro.TOTAL_CREDITO_SISTEMA) }}</h5>
              </div>
            </b-col>
            <b-col md="3">
              <div class="summary-card summary-card-danger shadow-sm mb-3">
                <small class="summary-label">Débito Sistema</small>
                <h5 class="mb-0 font-weight-bold text-danger">{{ formatarMoeda(registro.TOTAL_DEBITO_SISTEMA) }}</h5>
              </div>
            </b-col>
            <b-col md="3">
              <div class="summary-card summary-card-info shadow-sm mb-3">
                <small class="summary-label">Crédito Conferido</small>
                <h5 class="mb-0 font-weight-bold text-info">{{ formatarMoeda(registro.TOTAL_CREDITO_CONFERIDO) }}</h5>
              </div>
            </b-col>
            <b-col md="3">
              <div class="summary-card summary-card-warning shadow-sm mb-3">
                <small class="summary-label">Diferença Total</small>
                <h5 class="mb-0 font-weight-bold" :class="Math.abs(Number(registro.DIFERENCA_TOTAL || 0)) < 0.009 ? 'text-success' : 'text-warning'">{{ formatarMoeda(registro.DIFERENCA_TOTAL) }}</h5>
              </div>
            </b-col>
          </b-row>

          <b-card class="shadow-sm" :class="{'bg-dark border-secondary': isDark}">
            <div class="d-flex justify-content-between align-items-center mb-3 flex-wrap">
              <h5 class="mb-0">
                <i class="fa fa-list mr-2 text-info"></i>
                Itens da Conferência
              </h5>
              <div>
                <b-badge variant="secondary" pill class="mr-2">Itens: {{ itens.length }}</b-badge>
                <b-badge variant="success" pill class="mr-2">Confirmados: {{ itensConfirmados }}</b-badge>
                <b-badge variant="warning" pill>Com Diferença: {{ itensComDiferenca }}</b-badge>
              </div>
            </div>

            <b-table
              :items="itens"
              :fields="colunasItens"
              hover
              small
              responsive
              show-empty
              empty-text="Nenhum item carregado para a conta e período informados"
              class="mb-0">

              <template #cell(VALOR_SISTEMA)="row">
                <span class="font-weight-bold text-right d-block">{{ formatarMoeda(row.item.VALOR_SISTEMA) }}</span>
              </template>

              <template #cell(VALOR_CONFERIDO)="row">
                <vue-autonumeric
                  v-if="podeEditarItens"
                  v-model="row.item.VALOR_CONFERIDO"
                  :options="formatoMoeda"
                  :class="['form-control form-control-sm text-right rounded-pill shadow-xs']"
                  @input="atualizarItem(row.item)"
                  @blur.native="atualizarItem(row.item)">
                </vue-autonumeric>
                <span v-else class="text-right d-block">{{ formatarMoeda(row.item.VALOR_CONFERIDO) }}</span>
              </template>

              <template #cell(DIFERENCA)="row">
                <span class="font-weight-bold text-right d-block" :class="Math.abs(Number(row.item.DIFERENCA || 0)) < 0.009 ? 'text-success' : 'text-warning'">
                  {{ formatarMoeda(row.item.DIFERENCA) }}
                </span>
              </template>

              <template #cell(CONFIRMADO)="row">
                <b-badge :variant="row.item.CONFIRMADO === 'S' ? 'success' : 'warning'" pill>
                  {{ row.item.CONFIRMADO === 'S' ? 'Sim' : 'Não' }}
                </b-badge>
              </template>

              <template #cell(OBS)="row">
                <b-form-input
                  v-if="podeEditarItens"
                  v-model="row.item.OBS"
                  maxlength="250"
                  placeholder="Observação do item"
                  class="shadow-xs rounded-pill">
                </b-form-input>
                <span v-else>{{ row.item.OBS }}</span>
              </template>

              <template #cell(actions)="row">
                <b-button variant="outline-primary" pill size="sm" class="font-weight-bold" @click="abrirMovimentos(row.item)">
                  <i class="fa fa-search mr-1"></i> Movimentos
                </b-button>
              </template>
            </b-table>
          </b-card>
        </b-col>
      </b-row>

      <b-modal
        ref="modalMovimentos"
        id="modal-movimentos-conferencia-financeira"
        hide-header
        hide-footer
        body-class="p-0 overflow-hidden"
        content-class="border-0 shadow-lg"
        centered
        size="xl">

        <div class="modal-header-premium p-3 d-flex align-items-center bg-dark" style="background: linear-gradient(135deg, #20a8d8 0%, #1783ae 100%) !important;">
          <div class="header-icon-wrapper mr-3 bg-white-10">
            <i class="fa fa-list-alt text-white"></i>
          </div>
          <div>
            <h5 class="mb-0 font-weight-bold text-white">Movimentos Vinculados</h5>
            <small class="text-white opacity-75">Detalhamento dos lançamentos da RBANMOV1 do item selecionado</small>
          </div>
          <b-button variant="link" class="ml-auto text-white p-0 opacity-75" @click="$refs.modalMovimentos.hide()">
            <i class="fa fa-times fa-lg"></i>
          </b-button>
        </div>

        <div class="p-4 bg-white" v-if="itemSelecionado">
          <div class="modal-info-bar mb-4 bg-light border shadow-sm p-3 rounded">
            <b-row align-v="center">
              <b-col md="4">
                <small class="modal-label-premium">Agrupamento</small>
                <h5 class="mb-0 font-weight-bold text-dark">{{ itemSelecionado.TIPO_MOVIMENTO }} / {{ itemSelecionado.FORMA_DOCUMENTO }}</h5>
              </b-col>
              <b-col md="4" class="border-left">
                <small class="modal-label-premium">Valor Sistema</small>
                <h5 class="mb-0 font-weight-bold text-primary">{{ formatarMoeda(itemSelecionado.VALOR_SISTEMA) }}</h5>
              </b-col>
              <b-col md="4" class="border-left text-right">
                <small class="modal-label-premium">Valor Conferido</small>
                <h5 class="mb-0 font-weight-bold text-success">{{ formatarMoeda(itemSelecionado.VALOR_CONFERIDO) }}</h5>
              </b-col>
            </b-row>
          </div>

          <b-table
            :items="movimentosItem"
            :fields="colunasMovimentos"
            hover
            small
            responsive
            show-empty
            empty-text="Nenhum movimento vinculado a este item"
            class="mb-0 modal-table-premium">

            <template #cell(DT_MOV)="row">
              {{ formatarDataBR(row.item.DT_MOV) }}
            </template>

            <template #cell(CLIENTE_FORNECEDOR)="row">
              {{ formatarClienteFornecedor(row.item) }}
            </template>

            <template #cell(VL_MOV)="row">
              <span class="font-weight-bold text-right d-block">{{ formatarMoeda(row.item.VL_MOV) }}</span>
            </template>
          </b-table>
        </div>

        <div class="modal-footer-premium p-3 bg-light border-top d-flex justify-content-end">
          <b-button variant="soft-primary" pill class="px-4 font-weight-bold" @click="$refs.modalMovimentos.hide()">
            FECHAR
          </b-button>
        </div>
      </b-modal>

      <b-modal
        ref="modalConfirmar"
        id="modal-confirmar-conferencia-financeira"
        hide-header
        hide-footer
        body-class="p-0 overflow-hidden"
        content-class="border-0 shadow-lg"
        centered
        size="lg">

        <div class="modal-header-premium p-3 d-flex align-items-center bg-dark" style="background: linear-gradient(135deg, #198754 0%, #146c43 100%) !important;">
          <div class="header-icon-wrapper mr-3 bg-white-10">
            <i class="fa fa-lock text-white"></i>
          </div>
          <div>
            <h5 class="mb-0 font-weight-bold text-white">Confirmar Conferência</h5>
            <small class="text-white opacity-75">Após confirmar, os lançamentos vinculados ficam bloqueados</small>
          </div>
          <b-button variant="link" class="ml-auto text-white p-0 opacity-75" @click="$refs.modalConfirmar.hide()">
            <i class="fa fa-times fa-lg"></i>
          </b-button>
        </div>

        <div class="p-4 bg-white">
          <div class="modal-info-bar mb-4 bg-light border shadow-sm p-3 rounded">
            <b-row align-v="center">
              <b-col md="6">
                <small class="modal-label-premium">Conta / Período</small>
                <h5 class="mb-0 font-weight-bold text-dark">{{ registro.CONTA_CODIGONOME || registro.CONTA_NOME || registro.CONTA_ID }}</h5>
                <small class="text-muted font-weight-bold">{{ formatarDataBR(registro.DATA_INICIO) }} até {{ formatarDataBR(registro.DATA_FIM) }}</small>
              </b-col>
              <b-col md="3" class="border-left">
                <small class="modal-label-premium">Itens</small>
                <h5 class="mb-0 font-weight-bold text-primary">{{ itens.length }}</h5>
              </b-col>
              <b-col md="3" class="border-left text-right">
                <small class="modal-label-premium">Diferença Total</small>
                <h5 class="mb-0 font-weight-bold" :class="Math.abs(Number(registro.DIFERENCA_TOTAL || 0)) < 0.009 ? 'text-success' : 'text-warning'">{{ formatarMoeda(registro.DIFERENCA_TOTAL) }}</h5>
              </b-col>
            </b-row>
          </div>

          <div class="modal-section-title mb-3">
            <i class="fa fa-check-circle mr-2 text-success"></i> Revisão final
          </div>

          <p class="mb-0 text-muted">Confirme apenas após revisar os valores conferidos e as diferenças. Esta ação marca a conferência como fechada.</p>
        </div>

        <div class="modal-footer-premium p-3 bg-light border-top d-flex justify-content-between">
          <b-button variant="soft-primary" pill class="px-4 font-weight-bold" @click="$refs.modalConfirmar.hide()">
            FECHAR
          </b-button>
          <b-button variant="success" pill class="px-5 font-weight-bold shadow-sm" :disabled="confirmando" @click="executarConfirmacao">
            <i class="fa fa-check-circle mr-1"></i> CONFIRMAR CONFERÊNCIA
          </b-button>
        </div>
      </b-modal>

      <b-modal
        ref="modalReabrir"
        id="modal-reabrir-conferencia-financeira"
        hide-header
        hide-footer
        body-class="p-0 overflow-hidden"
        content-class="border-0 shadow-lg"
        centered
        size="lg"
        @shown="focarCampoMotivoReabertura">

        <div class="modal-header-premium p-3 d-flex align-items-center bg-warning" style="background: linear-gradient(135deg, #f0ad4e 0%, #d58512 100%) !important;">
          <div class="header-icon-wrapper mr-3 bg-white-10">
            <i class="fa fa-unlock-alt text-white"></i>
          </div>
          <div>
            <h5 class="mb-0 font-weight-bold text-white">Reabrir Conferência</h5>
            <small class="text-white opacity-75">Esta ação desfaz o fechamento e libera a conferência para edição</small>
          </div>
          <b-button variant="link" class="ml-auto text-white p-0 opacity-75" @click="$refs.modalReabrir.hide()">
            <i class="fa fa-times fa-lg"></i>
          </b-button>
        </div>

        <div class="p-4 bg-white">
          <div class="modal-info-bar mb-4 bg-light border shadow-sm p-3 rounded">
            <b-row align-v="center">
              <b-col md="7">
                <small class="modal-label-premium">Conta / Período</small>
                <h5 class="mb-0 font-weight-bold text-dark">{{ registro.CONTA_CODIGONOME || registro.CONTA_NOME || registro.CONTA_ID }}</h5>
                <small class="text-muted font-weight-bold">{{ formatarDataBR(registro.DATA_INICIO) }} até {{ formatarDataBR(registro.DATA_FIM) }}</small>
              </b-col>
              <b-col md="5" class="border-left text-right">
                <small class="modal-label-premium">Status Atual</small>
                <h5 class="mb-0 font-weight-bold text-warning">{{ getLabelStatus(registro.STATUS) }}</h5>
              </b-col>
            </b-row>
          </div>

          <div class="modal-section-title mb-3">
            <i class="fa fa-pencil mr-2 text-warning"></i> Motivo da reabertura
          </div>

          <b-form-textarea
            ref="motivoReabertura"
            v-model="motivoReabertura"
            rows="4"
            maxlength="500"
            placeholder="Descreva o motivo da reabertura"
            class="shadow-xs rounded">
          </b-form-textarea>
        </div>

        <div class="modal-footer-premium p-3 bg-light border-top d-flex justify-content-between">
          <b-button variant="soft-primary" pill class="px-4 font-weight-bold" @click="$refs.modalReabrir.hide()">
            FECHAR
          </b-button>
          <b-button variant="warning" pill class="px-5 font-weight-bold shadow-sm text-white" :disabled="reabrindo" @click="executarReabertura">
            <i class="fa fa-unlock-alt mr-1"></i> CONFIRMAR REABERTURA
          </b-button>
        </div>
      </b-modal>

      <b-modal
        ref="modalCancelar"
        id="modal-cancelar-cadastro-conferencia-financeira"
        hide-header
        hide-footer
        body-class="p-0 overflow-hidden"
        content-class="border-0 shadow-lg"
        centered
        size="lg"
        @shown="focarCampoMotivoCancelamento">

        <div class="modal-header-premium p-3 d-flex align-items-center bg-danger" style="background: linear-gradient(135deg, #dc3545 0%, #a71d2a 100%) !important;">
          <div class="header-icon-wrapper mr-3 bg-white-10">
            <i class="fa fa-ban text-white"></i>
          </div>
          <div>
            <h5 class="mb-0 font-weight-bold text-white">Cancelar Conferência</h5>
            <small class="text-white opacity-75">Informe um motivo para registrar o cancelamento</small>
          </div>
          <b-button variant="link" class="ml-auto text-white p-0 opacity-75" @click="$refs.modalCancelar.hide()">
            <i class="fa fa-times fa-lg"></i>
          </b-button>
        </div>

        <div class="p-4 bg-white">
          <div class="modal-info-bar mb-4 bg-light border shadow-sm p-3 rounded">
            <b-row align-v="center">
              <b-col md="7">
                <small class="modal-label-premium">Conta / Período</small>
                <h5 class="mb-0 font-weight-bold text-dark">{{ registro.CONTA_CODIGONOME || registro.CONTA_NOME || registro.CONTA_ID }}</h5>
                <small class="text-muted font-weight-bold">{{ formatarDataBR(registro.DATA_INICIO) }} até {{ formatarDataBR(registro.DATA_FIM) }}</small>
              </b-col>
              <b-col md="5" class="border-left text-right">
                <small class="modal-label-premium">Status Atual</small>
                <h5 class="mb-0 font-weight-bold text-danger">{{ getLabelStatus(registro.STATUS) }}</h5>
              </b-col>
            </b-row>
          </div>

          <div class="modal-section-title mb-3">
            <i class="fa fa-pencil mr-2 text-danger"></i> Motivo do cancelamento
          </div>

          <b-form-textarea
            ref="motivoCancelamento"
            v-model="motivoCancelamento"
            rows="4"
            maxlength="500"
            placeholder="Descreva o motivo do cancelamento"
            class="shadow-xs rounded">
          </b-form-textarea>
        </div>

        <div class="modal-footer-premium p-3 bg-light border-top d-flex justify-content-between">
          <b-button variant="soft-primary" pill class="px-4 font-weight-bold" @click="$refs.modalCancelar.hide()">
            FECHAR
          </b-button>
          <b-button variant="danger" pill class="px-5 font-weight-bold shadow-sm" :disabled="cancelando" @click="executarCancelamento">
            <i class="fa fa-ban mr-1"></i> CONFIRMAR CANCELAMENTO
          </b-button>
        </div>
      </b-modal>
    </div>
  </div>
</template>

<script>
import HeaderPadrao from '@/components/Cec/HeaderPadrao.vue'
import CecInputData from '@/components/Cec/CecInputData.vue'
import ContaDropDown from '@/views/componentes/dropdown/ContaDropDown.vue'
import VueAutonumeric from 'vue-autonumeric'
import * as Tools from '@/tools/tools.js'
import {
  STATUS_CONFERENCIA_FINANCEIRA,
  getLabelStatusConferenciaFinanceira,
  getVariantStatusConferenciaFinanceira
} from '@/utils/conferenciaFinanceiraStatus'

function escapeHtml(valor) {
  return String(valor == null ? '' : valor)
    .replace(/&/g, '&amp;')
    .replace(/</g, '&lt;')
    .replace(/>/g, '&gt;')
    .replace(/"/g, '&quot;')
    .replace(/'/g, '&#39;')
}

function formatarDataHoraRelatorio() {
  return new Date().toLocaleString('pt-BR')
}

function criarRegistroPadrao() {
  return {
    ID: null,
    CONTA_ID: null,
    CONTA_NOME: null,
    CONTA_CODIGONOME: null,
    DATA_INICIO: '',
    DATA_FIM: '',
    STATUS: STATUS_CONFERENCIA_FINANCEIRA.ABERTA,
    OBS: '',
    TOTAL_CREDITO_SISTEMA: 0,
    TOTAL_DEBITO_SISTEMA: 0,
    TOTAL_CREDITO_CONFERIDO: 0,
    TOTAL_DEBITO_CONFERIDO: 0,
    DIFERENCA_TOTAL: 0,
    USUARIO_CADASTRO: null,
    USUARIO_CONFIRMOU: null
  }
}

export default {
  components: {
    HeaderPadrao,
    CecInputData,
    ContaDropDown,
    VueAutonumeric
  },
  data() {
    return {
      statusConferencia: STATUS_CONFERENCIA_FINANCEIRA,
      registro: criarRegistroPadrao(),
      itens: [],
      itemSelecionado: null,
      movimentosItem: [],
      carregandoResumo: false,
      salvando: false,
      confirmando: false,
      reabrindo: false,
      cancelando: false,
      permiteReabrirConferencia: false,
      motivoCancelamento: '',
      motivoReabertura: '',
      empresaPreferencial: null,
      ultimaConferenciaConta: null,
      mensagemPeriodoSugerido: 'Informe a conta para sugerir automaticamente o próximo período.',
      atualizandoPeriodoAutomaticamente: false,
      periodoBloqueadoAutomaticamente: false,
      formatoMoeda: {
        digitGroupSeparator: '.',
        decimalCharacter: ',',
        decimalCharacterAlternative: '.',
        currencySymbol: '',
        decimalPlaces: 2,
        minimumValue: '0'
      },
      colunasItens: [
        { key: 'TIPO_MOVIMENTO', label: 'Tipo Movimento', thStyle: { width: '140px' } },
        { key: 'FORMA_DOCUMENTO', label: 'Forma/Documento', thStyle: { width: '170px' } },
        { key: 'VALOR_SISTEMA', label: 'Valor Sistema', class: 'text-right', thStyle: { width: '140px' } },
        { key: 'VALOR_CONFERIDO', label: 'Valor Conferido', thStyle: { width: '180px' } },
        { key: 'DIFERENCA', label: 'Diferença', class: 'text-right', thStyle: { width: '140px' } },
        { key: 'CONFIRMADO', label: 'Confirmado', class: 'text-center', thStyle: { width: '120px' } },
        { key: 'OBS', label: 'Observação' },
        { key: 'actions', label: 'Movimentos', class: 'text-center', thStyle: { width: '130px' } }
      ],
      colunasMovimentos: [
        { key: 'CONTADOR', label: 'Contador', thStyle: { width: '90px' } },
        { key: 'DT_MOV', label: 'Data', thStyle: { width: '110px' } },
        { key: 'DOCUMENTO', label: 'Documento', thStyle: { width: '150px' } },
        { key: 'CLIENTE_FORNECEDOR', label: 'Cliente/Fornecedor', thStyle: { width: '220px' } },
        { key: 'FORMA', label: 'Forma', thStyle: { width: '140px' } },
        { key: 'VL_MOV', label: 'Valor', class: 'text-right', thStyle: { width: '130px' } },
        { key: 'HISTORICO', label: 'Histórico' },
        { key: 'USUARIO_INCLUSAO', label: 'Usuário', thStyle: { width: '140px' } }
      ]
    }
  },
  computed: {
    isDark() {
      return this.$store.getters['tema/IS_DARK']
    },
    resumoCarregado() {
      return this.itens.length > 0
    },
    podeEditarCabecalho() {
      return this.registro.STATUS === this.statusConferencia.ABERTA && !this.resumoCarregado
    },
    podeEditarPeriodo() {
      return this.podeEditarCabecalho && !this.periodoBloqueadoAutomaticamente
    },
    podeEditarDataFinal() {
      if (!this.podeEditarCabecalho) return false
      if (!this.ultimaConferenciaConta?.DATA_FIM) return true

      const hoje = this.converterDataParaComparacao(Tools.getDataAtual())
      const ultimaDataFim = this.converterDataParaComparacao(this.ultimaConferenciaConta.DATA_FIM)
      return !!(ultimaDataFim && hoje && ultimaDataFim < hoje)
    },
    podeEditarItens() {
      return this.registro.STATUS === this.statusConferencia.ABERTA
    },
    podeCarregarResumo() {
      return this.podeEditarCabecalho && !!this.registro.CONTA_ID && !!this.registro.DATA_INICIO && !!this.registro.DATA_FIM
    },
    podeSalvar() {
      return this.podeEditarItens && !!this.registro.CONTA_ID && this.itens.length > 0
    },
    podeConfirmar() {
      return this.podeSalvar
    },
    podeReabrir() {
      return !!this.registro.ID && this.registro.STATUS === this.statusConferencia.FECHADA && this.permiteReabrirConferencia
    },
    itensConfirmados() {
      return this.itens.filter(item => item.CONFIRMADO === 'S').length
    },
    itensComDiferenca() {
      return this.itens.filter(item => Math.abs(Number(item.DIFERENCA || 0)) >= 0.009).length
    }
  },
  methods: {
    normalizarItem(item) {
      const valorSistema = Number(item.VALOR_SISTEMA || 0)
      const valorConferido = Number(item.VALOR_CONFERIDO == null ? valorSistema : item.VALOR_CONFERIDO)
      const diferenca = Number((valorConferido - valorSistema).toFixed(2))
      return {
        ...item,
        VALOR_SISTEMA: valorSistema,
        VALOR_CONFERIDO: valorConferido,
        DIFERENCA: diferenca,
        CONFIRMADO: Math.abs(diferenca) < 0.009 ? 'S' : 'N',
        MOVIMENTOS: Array.isArray(item.MOVIMENTOS) ? item.MOVIMENTOS : []
      }
    },
    atualizarTotais() {
      const totais = this.itens.reduce((acc, item) => {
        const valorSistema = Number(item.VALOR_SISTEMA || 0)
        const valorConferido = Number(item.VALOR_CONFERIDO || 0)

        if (String(item.TIPO_MOVIMENTO || '').trim() === 'CREDITO') {
          acc.TOTAL_CREDITO_SISTEMA += valorSistema
          acc.TOTAL_CREDITO_CONFERIDO += valorConferido
        }

        if (String(item.TIPO_MOVIMENTO || '').trim() === 'DEBITO') {
          acc.TOTAL_DEBITO_SISTEMA += valorSistema
          acc.TOTAL_DEBITO_CONFERIDO += valorConferido
        }

        acc.DIFERENCA_TOTAL += Number(item.DIFERENCA || 0)
        return acc
      }, {
        TOTAL_CREDITO_SISTEMA: 0,
        TOTAL_DEBITO_SISTEMA: 0,
        TOTAL_CREDITO_CONFERIDO: 0,
        TOTAL_DEBITO_CONFERIDO: 0,
        DIFERENCA_TOTAL: 0
      })

      Object.assign(this.registro, totais)
    },
    atualizarItem(item) {
      item.VALOR_CONFERIDO = Number(item.VALOR_CONFERIDO || 0)
      item.DIFERENCA = Number((item.VALOR_CONFERIDO - Number(item.VALOR_SISTEMA || 0)).toFixed(2))
      item.CONFIRMADO = Math.abs(item.DIFERENCA) < 0.009 ? 'S' : 'N'
      this.atualizarTotais()
    },
    limparResumoCarregado() {
      this.itens = []
      this.itemSelecionado = null
      this.movimentosItem = []
      Object.assign(this.registro, {
        TOTAL_CREDITO_SISTEMA: 0,
        TOTAL_DEBITO_SISTEMA: 0,
        TOTAL_CREDITO_CONFERIDO: 0,
        TOTAL_DEBITO_CONFERIDO: 0,
        DIFERENCA_TOTAL: 0
      })
    },
    aplicarPeriodoAutomatico(dataInicio, dataFim, mensagem) {
      this.atualizandoPeriodoAutomaticamente = true
      this.registro.DATA_INICIO = dataInicio
      this.registro.DATA_FIM = dataFim
      this.mensagemPeriodoSugerido = mensagem
      this.$nextTick(() => {
        this.atualizandoPeriodoAutomaticamente = false
      })
    },
    registrarEdicaoManualPeriodo() {
      if (this.atualizandoPeriodoAutomaticamente || this.registro.ID) return
      this.limparResumoCarregado()
      this.mensagemPeriodoSugerido = 'Período definido manualmente. Use Carregar Resumo para atualizar os movimentos da conta.'
    },
    converterDataParaComparacao(dataTexto) {
      if (!dataTexto) return null
      const data = new Date(`${dataTexto}T00:00:00`)
      return Number.isNaN(data.getTime()) ? null : data
    },
    async carregarPeriodoSugerido(contaId = this.registro.CONTA_ID) {
      const hoje = Tools.getDataAtual()

      if (!contaId) {
        this.ultimaConferenciaConta = null
        this.periodoBloqueadoAutomaticamente = false
        this.aplicarPeriodoAutomatico(
          this.registro.DATA_INICIO || hoje,
          this.registro.DATA_FIM || hoje,
          'Informe a conta para sugerir automaticamente o próximo período. Sem histórico, o período pode ser definido livremente.'
        )
        return
      }

      try {
        const { data } = await this.$http.get('/api/protected/conferenciaFinanceira/periodo-sugerido', {
          params: { contaId }
        })

        const dataInicio = data.dataInicioSugerida || hoje
        const dataFim = data.dataFimSugerida || hoje
        const ultima = data.ultimaConferenciaConta
        this.ultimaConferenciaConta = ultima || null
        this.periodoBloqueadoAutomaticamente = !!ultima
        const mensagem = ultima
          ? `Última conferência da conta: ${this.formatarDataBR(ultima.DATA_INICIO)} até ${this.formatarDataBR(ultima.DATA_FIM)}. O próximo período foi sugerido automaticamente e ficou bloqueado até novo cadastro.`
          : 'Nenhuma conferência anterior foi encontrada para esta conta. O período pode ser informado livremente.'

        this.aplicarPeriodoAutomatico(dataInicio, dataFim, mensagem)
      } catch (err) {
        this.ultimaConferenciaConta = null
        this.periodoBloqueadoAutomaticamente = false
        this.aplicarPeriodoAutomatico(hoje, hoje, 'Não foi possível obter o período sugerido. Informe o período manualmente.')
      }
    },
    async carregarRegistro(id) {
      const { data } = await this.$http.get(`/api/protected/conferenciaFinanceira/${id}`)
      this.registro = { ...criarRegistroPadrao(), ...(data.cabecalho || {}) }
      this.ultimaConferenciaConta = data?.cabecalho?.DATA_FIM ? { DATA_FIM: data.cabecalho.DATA_FIM } : null
      this.itens = (data.itens || []).map(this.normalizarItem)
      this.atualizarTotais()
    },
    async carregarResumo() {
      const hoje = this.converterDataParaComparacao(Tools.getDataAtual())
      const dataInicio = this.converterDataParaComparacao(this.registro.DATA_INICIO)
      const dataFim = this.converterDataParaComparacao(this.registro.DATA_FIM)

      if (!this.registro.CONTA_ID) {
        Tools.exibirMensagem(this.$store, 'Aviso', 'warning', 'Informe a conta da conferência.')
        return
      }
      if (!this.registro.DATA_INICIO || !this.registro.DATA_FIM) {
        Tools.exibirMensagem(this.$store, 'Aviso', 'warning', 'Informe o período da conferência.')
        return
      }
      if (dataInicio && hoje && dataInicio > hoje) {
        Tools.exibirMensagem(this.$store, 'Aviso', 'warning', 'A data inicial não pode ser maior que hoje.')
        return
      }
      if (dataFim && hoje && dataFim > hoje) {
        Tools.exibirMensagem(this.$store, 'Aviso', 'warning', 'A data final não pode ser maior que hoje.')
        return
      }
      if (dataInicio && dataFim && dataFim < dataInicio) {
        Tools.exibirMensagem(this.$store, 'Aviso', 'warning', 'A data final não pode ser menor que a data inicial.')
        return
      }
      if (dataInicio && dataFim && dataInicio > dataFim) {
        Tools.exibirMensagem(this.$store, 'Aviso', 'warning', 'A data inicial não pode ser maior que a data final.')
        return
      }

      this.carregandoResumo = true
      try {
        const { data } = await this.$http.get('/api/protected/conferenciaFinanceira/resumo', {
          params: {
            contaId: this.registro.CONTA_ID,
            dt1: this.registro.DATA_INICIO,
            dt2: this.registro.DATA_FIM,
            conferenciaId: this.registro.ID || null
          }
        })

        this.itens = (data.itens || []).map(this.normalizarItem)
        this.atualizarTotais()
      } catch (err) {
        Tools.exibirMensagem(this.$store, 'Erro', 'danger', err)
      } finally {
        this.carregandoResumo = false
      }
    },
    montarPayload() {
      return {
        registro: {
          ID: this.registro.ID,
          CONTA_ID: this.registro.CONTA_ID,
          DATA_INICIO: this.registro.DATA_INICIO,
          DATA_FIM: this.registro.DATA_FIM,
          STATUS: this.registro.STATUS,
          OBS: this.registro.OBS,
          TOTAL_CREDITO_SISTEMA: this.registro.TOTAL_CREDITO_SISTEMA,
          TOTAL_DEBITO_SISTEMA: this.registro.TOTAL_DEBITO_SISTEMA,
          TOTAL_CREDITO_CONFERIDO: this.registro.TOTAL_CREDITO_CONFERIDO,
          TOTAL_DEBITO_CONFERIDO: this.registro.TOTAL_DEBITO_CONFERIDO,
          DIFERENCA_TOTAL: this.registro.DIFERENCA_TOTAL
        },
        itens: this.itens.map(item => ({
          ID: item.ID,
          TIPO_MOVIMENTO: item.TIPO_MOVIMENTO,
          FORMA_DOCUMENTO: item.FORMA_DOCUMENTO,
          VALOR_SISTEMA: item.VALOR_SISTEMA,
          VALOR_CONFERIDO: item.VALOR_CONFERIDO,
          DIFERENCA: item.DIFERENCA,
          CONFIRMADO: item.CONFIRMADO,
          OBS: item.OBS,
          MOVIMENTOS: (item.MOVIMENTOS || []).map(movimento => ({
            CONTADOR: movimento.CONTADOR || movimento.RBANMOV1_ID
          }))
        })),
        login: window.localStorage.getItem('login') || 'sistema'
      }
    },
    async salvar(exibirMensagem = true) {
      this.salvando = true
      try {
        const { data } = await this.$http.post('/api/protected/conferenciaFinanceira', this.montarPayload())
        this.registro = { ...criarRegistroPadrao(), ...(data.cabecalho || {}) }
        this.itens = (data.itens || []).map(this.normalizarItem)
        this.atualizarTotais()
        this.$store.commit('conferenciaFinanceira/SET_RECARREGAR_LISTA', true)

        if (this.registro.ID) {
          this.$router.replace(`/movimentos/conferencia-financeira/edit/${this.registro.ID}`)
        }

        if (exibirMensagem) {
          Tools.exibirMensagem(this.$store, 'Sucesso', 'success', 'Conferência financeira salva com sucesso.')
        }
      } catch (err) {
        Tools.exibirMensagem(this.$store, 'Erro', 'danger', err)
        if (!exibirMensagem) throw err
      } finally {
        this.salvando = false
      }
    },
    abrirModalConfirmar() {
      if (!this.podeConfirmar) return
      this.$refs.modalConfirmar.show()
    },
    async executarConfirmacao() {
      this.confirmando = true
      try {
        if (!this.registro.ID) {
          await this.salvar(false)
        }

        if (!this.registro.ID) return

        const { data } = await this.$http.post(`/api/protected/conferenciaFinanceira/${this.registro.ID}/confirmar`, {
          login: window.localStorage.getItem('login') || 'sistema'
        })
        this.registro = { ...criarRegistroPadrao(), ...(data.cabecalho || {}) }
        this.itens = (data.itens || []).map(this.normalizarItem)
        this.atualizarTotais()
        this.$refs.modalConfirmar.hide()
        this.$store.commit('conferenciaFinanceira/SET_RECARREGAR_LISTA', true)
        Tools.exibirMensagem(this.$store, 'Sucesso', 'success', 'Conferência financeira confirmada com sucesso.')
      } catch (err) {
        Tools.exibirMensagem(this.$store, 'Erro', 'danger', err)
      } finally {
        this.confirmando = false
      }
    },
    async validarPermissaoReabrirConferencia() {
      const permissaoPayLoad = {
        login: window.localStorage.getItem('login'),
        permissao: 'AcPropReabrirConferenciaFinanceira'
      }

      try {
        const permissaoResult = await this.$http.post('api/protected/seguranca/validarPermissao', permissaoPayLoad)
        this.permiteReabrirConferencia = permissaoResult?.data?.permissao === true
      } catch (err) {
        this.permiteReabrirConferencia = false
      }
    },
    abrirModalReabrir() {
      if (!this.podeReabrir) return
      this.motivoReabertura = ''
      this.$refs.modalReabrir.show()
    },
    async executarReabertura() {
      if (!String(this.motivoReabertura || '').trim()) {
        Tools.exibirMensagem(this.$store, 'Aviso', 'warning', 'Informe o motivo da reabertura.')
        return
      }

      this.reabrindo = true
      try {
        const { data } = await this.$http.post(`/api/protected/conferenciaFinanceira/${this.registro.ID}/reabrir`, {
          login: window.localStorage.getItem('login') || 'sistema',
          motivo: this.motivoReabertura.trim()
        })

        this.registro = { ...criarRegistroPadrao(), ...(data.cabecalho || {}) }
        this.itens = (data.itens || []).map(this.normalizarItem)
        this.atualizarTotais()
        this.$refs.modalReabrir.hide()
        this.$store.commit('conferenciaFinanceira/SET_RECARREGAR_LISTA', true)
        Tools.exibirMensagem(this.$store, 'Sucesso', 'success', 'Conferência financeira reaberta com sucesso.')
      } catch (err) {
        Tools.exibirMensagem(this.$store, 'Erro', 'danger', err)
      } finally {
        this.reabrindo = false
      }
    },
    abrirModalCancelar() {
      this.motivoCancelamento = ''
      this.$refs.modalCancelar.show()
    },
    async executarCancelamento() {
      if (!String(this.motivoCancelamento || '').trim()) {
        Tools.exibirMensagem(this.$store, 'Aviso', 'warning', 'Informe o motivo do cancelamento.')
        return
      }

      this.cancelando = true
      try {
        const { data } = await this.$http.post(`/api/protected/conferenciaFinanceira/${this.registro.ID}/cancelar`, {
          login: window.localStorage.getItem('login') || 'sistema',
          motivo: this.motivoCancelamento
        })
        this.registro = { ...criarRegistroPadrao(), ...(data.cabecalho || {}) }
        this.itens = (data.itens || []).map(this.normalizarItem)
        this.atualizarTotais()
        this.$refs.modalCancelar.hide()
        this.$store.commit('conferenciaFinanceira/SET_RECARREGAR_LISTA', true)
        Tools.exibirMensagem(this.$store, 'Sucesso', 'success', 'Conferência financeira cancelada com sucesso.')
      } catch (err) {
        Tools.exibirMensagem(this.$store, 'Erro', 'danger', err)
      } finally {
        this.cancelando = false
      }
    },
    focarCampoMotivoCancelamento() {
      this.$nextTick(() => {
        if (this.$refs.motivoCancelamento && typeof this.$refs.motivoCancelamento.focus === 'function') {
          this.$refs.motivoCancelamento.focus()
        }
      })
    },
    focarCampoMotivoReabertura() {
      this.$nextTick(() => {
        if (this.$refs.motivoReabertura && typeof this.$refs.motivoReabertura.focus === 'function') {
          this.$refs.motivoReabertura.focus()
        }
      })
    },
    async abrirMovimentos(item) {
      this.itemSelecionado = item
      try {
        if (this.registro.ID && item.ID) {
          const { data } = await this.$http.get(`/api/protected/conferenciaFinanceira/${this.registro.ID}/item/${item.ID}/movimentos`)
          this.movimentosItem = data || []
        } else {
          this.movimentosItem = item.MOVIMENTOS || []
        }
        this.$refs.modalMovimentos.show()
      } catch (err) {
        Tools.exibirMensagem(this.$store, 'Erro', 'danger', err)
      }
    },
    async obterEmpresaPreferencial() {
      if (this.empresaPreferencial) return this.empresaPreferencial
      const { data } = await this.$http.get('/api/protected/conferenciaFinanceira/empresa-preferencial')
      this.empresaPreferencial = data || {}
      return this.empresaPreferencial
    },
    montarHtmlEspelhoConferencia(empresa) {
      const nomeUsuario = window.localStorage.getItem('nomeusuario') || window.localStorage.getItem('login') || 'sistema'
      const linhasItens = this.itens.map((item, index) => `
        <tr>
          <td>${index + 1}</td>
          <td>${escapeHtml(item.TIPO_MOVIMENTO || '')}</td>
          <td>${escapeHtml(item.FORMA_DOCUMENTO || '')}</td>
          <td style="text-align:right;">${escapeHtml(this.formatarMoeda(item.VALOR_SISTEMA || 0))}</td>
          <td style="text-align:right;">${escapeHtml(this.formatarMoeda(item.VALOR_CONFERIDO || 0))}</td>
          <td style="text-align:right;">${escapeHtml(this.formatarMoeda(item.DIFERENCA || 0))}</td>
          <td style="text-align:center;">${escapeHtml(item.CONFIRMADO === 'S' ? 'Sim' : 'Não')}</td>
          <td>${escapeHtml(item.OBS || '')}</td>
        </tr>
      `).join('')

      return `<!doctype html>
      <html lang="pt-BR">
      <head>
        <meta charset="utf-8">
        <title>Espelho da Conferência Financeira</title>
        <style>
          body { font-family: Arial, Helvetica, sans-serif; margin: 16px; color: #111; }
          .report-header { border: 1px solid #2f2f2f; }
          .report-header-top, .report-header-sub { display: flex; justify-content: space-between; padding: 4px 8px; font-size: 11px; border-bottom: 1px solid #2f2f2f; }
          .report-header-title { padding: 4px 8px; font-size: 12px; font-weight: 700; text-transform: uppercase; }
          .section { margin-top: 10px; }
          .section-title { font-size: 11px; font-weight: 700; margin-bottom: 4px; text-transform: uppercase; }
          .grid, .summary, .info-table { width: 100%; border-collapse: collapse; }
          .grid th, .grid td, .summary td, .info-table td { border: 1px solid #2f2f2f; padding: 4px 6px; font-size: 11px; }
          .grid th { background: #f2f2f2; text-align: left; }
          .summary td, .info-table td { width: 50%; }
          .signature-area { margin-top: 40px; }
          .signature-line { border-top: 1px solid #2f2f2f; width: 320px; padding-top: 6px; font-size: 11px; text-align: center; }
          .report-footer { position: fixed; left: 16px; right: 16px; bottom: 8px; display: flex; justify-content: space-between; font-size: 10px; border-top: 1px solid #2f2f2f; padding-top: 4px; background: #fff; }
          @media print { @page { size: A4 portrait; margin: 12mm; } body { margin: 0; } }
        </style>
      </head>
      <body>
        <div class="report-header">
          <div class="report-header-top">
            <div><strong>${escapeHtml(empresa.FANTASIA || empresa.RAZAO_RELATORIO || empresa.RAZAO || 'CECWEB')}</strong></div>
            <div><strong>Usuário:</strong> ${escapeHtml(nomeUsuario)}</div>
          </div>
          <div class="report-header-title">Espelho da Conferência Financeira</div>
          <div class="report-header-sub">
            <div>${escapeHtml(empresa.NOME_EMPRESA || '')}</div>
            <div><strong>Emitido em:</strong> ${escapeHtml(formatarDataHoraRelatorio())}</div>
          </div>
          <div class="report-header-sub" style="border-bottom:0;">
            <div><strong>Conta:</strong> ${escapeHtml(this.registro.CONTA_CODIGONOME || this.registro.CONTA_NOME || this.registro.CONTA_ID || '')}</div>
            <div><strong>Período:</strong> ${escapeHtml(this.formatarDataBR(this.registro.DATA_INICIO))} até ${escapeHtml(this.formatarDataBR(this.registro.DATA_FIM))}</div>
          </div>
        </div>

        <div class="section">
          <div class="section-title">Resumo da conferência</div>
          <table class="summary">
            <tr>
              <td><strong>Crédito Sistema:</strong> ${escapeHtml(this.formatarMoeda(this.registro.TOTAL_CREDITO_SISTEMA || 0))}</td>
              <td><strong>Débito Sistema:</strong> ${escapeHtml(this.formatarMoeda(this.registro.TOTAL_DEBITO_SISTEMA || 0))}</td>
            </tr>
            <tr>
              <td><strong>Crédito Conferido:</strong> ${escapeHtml(this.formatarMoeda(this.registro.TOTAL_CREDITO_CONFERIDO || 0))}</td>
              <td><strong>Débito Conferido:</strong> ${escapeHtml(this.formatarMoeda(this.registro.TOTAL_DEBITO_CONFERIDO || 0))}</td>
            </tr>
            <tr>
              <td><strong>Diferença Total:</strong> ${escapeHtml(this.formatarMoeda(this.registro.DIFERENCA_TOTAL || 0))}</td>
              <td><strong>Status:</strong> ${escapeHtml(this.getLabelStatus(this.registro.STATUS))}</td>
            </tr>
          </table>
        </div>

        <div class="section">
          <div class="section-title">Dados complementares</div>
          <table class="info-table">
            <tr>
              <td><strong>ID:</strong> ${escapeHtml(this.registro.ID || 'Novo')}</td>
              <td><strong>Itens:</strong> ${escapeHtml(this.itens.length)}</td>
            </tr>
            <tr>
              <td colspan="2"><strong>Observação:</strong> ${escapeHtml(this.registro.OBS || '')}</td>
            </tr>
          </table>
        </div>

        <div class="section">
          <div class="section-title">Resumo dos itens</div>
          <table class="grid">
            <thead>
              <tr>
                <th style="width:36px;">#</th>
                <th style="width:110px;">Tipo</th>
                <th style="width:150px;">Forma/Documento</th>
                <th style="width:120px; text-align:right;">Valor Sistema</th>
                <th style="width:120px; text-align:right;">Valor Conferido</th>
                <th style="width:110px; text-align:right;">Diferença</th>
                <th style="width:80px; text-align:center;">Confirmado</th>
                <th>Observação</th>
              </tr>
            </thead>
            <tbody>
              ${linhasItens}
            </tbody>
          </table>
        </div>

        <div class="signature-area">
          <div class="signature-line">Assinatura do usuário responsável</div>
        </div>

        <div class="report-footer">
          <div>Impresso em: ${escapeHtml(formatarDataHoraRelatorio())}</div>
          <div>Pág: 1 de 1</div>
          <div>Usuário: ${escapeHtml(nomeUsuario)}</div>
        </div>
      </body>
      </html>`
    },
    async imprimirEspelhoConferencia() {
      if (!this.itens.length) {
        Tools.exibirMensagem(this.$store, 'Aviso', 'warning', 'Carregue o resumo da conferência antes de gerar o espelho.')
        return
      }

      const janela = window.open('', '_blank')
      if (!janela) {
        Tools.exibirMensagem(this.$store, 'Aviso', 'warning', 'O navegador bloqueou a abertura da nova aba do espelho.')
        return
      }

      try {
        const empresa = await this.obterEmpresaPreferencial()
        const html = this.montarHtmlEspelhoConferencia(empresa)
        janela.document.open('text/html', 'replace')
        janela.document.write(html)
        janela.document.close()
        janela.focus()
      } catch (err) {
        janela.close()
        Tools.exibirMensagem(this.$store, 'Erro', 'danger', err)
      }
    },
    voltar() {
      this.$router.push('/movimentos/conferencia-financeira/listagem')
    },
    getLabelStatus(status) {
      return getLabelStatusConferenciaFinanceira(status)
    },
    getVariantStatus(status) {
      return getVariantStatusConferenciaFinanceira(status)
    },
    formatarDataBR(data) {
      return Tools.formatarDataBR(data)
    },
    formatarMoeda(valor) {
      return new Intl.NumberFormat('pt-BR', { style: 'currency', currency: 'BRL' }).format(Number(valor || 0))
    },
    formatarClienteFornecedor(movimento) {
      const codigo = movimento?.CLIENTE_ID || movimento?.FORNECEDOR_ID || null
      const nome = movimento?.NOME_CLIENTE || movimento?.NOME_FORNECEDOR || ''

      if (codigo && nome) return `${codigo} - ${nome}`
      if (codigo) return String(codigo)
      if (nome) return nome
      return '-'
    }
  },
  async created() {
    await this.validarPermissaoReabrirConferencia()
    const id = parseInt(this.$route.params.id, 10)
    if (id) {
      await this.carregarRegistro(id)
      return
    }

    await this.carregarPeriodoSugerido()
  },

  watch: {
    'registro.CONTA_ID': async function (novoValor, valorAnterior) {
      if (novoValor === valorAnterior || this.registro.ID) return
      this.limparResumoCarregado()
      await this.carregarPeriodoSugerido(novoValor)
    },
    'registro.DATA_INICIO': function (novoValor, valorAnterior) {
      if (novoValor === valorAnterior) return
      this.registrarEdicaoManualPeriodo()
    },
    'registro.DATA_FIM': function (novoValor, valorAnterior) {
      if (novoValor === valorAnterior) return
      this.registrarEdicaoManualPeriodo()
    }
  }
}
</script>

<style scoped>
.summary-card {
  background: #fff;
  border-radius: 14px;
  border: 1px solid #e9ecef;
  padding: 1rem 1.1rem;
  position: relative;
  overflow: hidden;
}

.summary-card::before {
  content: '';
  position: absolute;
  left: 0;
  top: 0;
  bottom: 0;
  width: 4px;
}

.summary-card-success::before {
  background: #198754;
}

.summary-card-danger::before {
  background: #dc3545;
}

.summary-card-info::before {
  background: #0dcaf0;
}

.summary-card-warning::before {
  background: #ffc107;
}

.summary-label {
  font-size: 0.75rem;
  font-weight: 700;
  text-transform: uppercase;
  letter-spacing: 0.5px;
  color: #6c757d;
  display: block;
  margin-bottom: 0.4rem;
}

.modal-header-premium {
  border-bottom: none;
}

.header-icon-wrapper {
  width: 45px;
  height: 45px;
  display: flex;
  align-items: center;
  justify-content: center;
  border-radius: 12px;
  font-size: 1.5rem;
}

.bg-white-10 {
  background-color: rgba(255, 255, 255, 0.15) !important;
}

.modal-info-bar {
  border-radius: 14px;
}

.modal-section-title {
  font-size: 0.9rem;
  font-weight: 700;
  color: #2d3748;
  border-bottom: 2px solid #edf2f7;
  padding-bottom: 0.5rem;
  text-transform: uppercase;
  letter-spacing: 0.5px;
}

.modal-label-premium {
  font-size: 0.75rem;
  font-weight: 700;
  text-transform: uppercase;
  letter-spacing: 0.5px;
  color: #6c757d;
  margin-bottom: 0.4rem;
  display: block;
}

.modal-footer-premium {
  border-top: 1px solid #e9ecef;
}

.shadow-xs {
  box-shadow: 0 2px 4px rgba(0, 0, 0, 0.05) !important;
}
</style>


// WEBPACK FOOTER //
// src/views/financeiro/conferencia-financeira/ConferenciaFinanceiraCadastro.vue